import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchBranches } from '../data-provider';
import type { Branch, Branches, BranchFilters } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal branch lookup cache used to resolve branch references
 * without repeatedly requesting the same branch data.
 *
 * Backed by React Query today, but can be migrated to another storage
 * implementation (e.g. IndexedDB) without changing consumers.
 */

export const branchesQueryOptions = (
  filters: Partial<BranchFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const branches = await fetchBranches(filters, { signal });
      put(branches.list);
      return branches;
    },
    queryKey: CACHE_KEYS.branches(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_BRANCHES_CACHE_KEY = btoa('internal-branches-cache-key');

const INTERNAL_QUERY_BRANCHES_KEY = branchesQueryOptions({ branchDesc: INTERNAL_BRANCHES_CACHE_KEY }).queryKey;

const getBranchFallback = (branchId: string): Branch => {
  logger.warn(`Branch "${branchId}" was not found. Verify the data model.`);
  return {
    branchDesc: branchId,
    customerKey: 0,
    id: branchId,
    label: branchId,
  };
};

export const put = async (branches: Branch[]) => {
  // Sync with storage (currently React Query; may use IndexedDB in the future).
  const data = queryClient.getQueryData(INTERNAL_QUERY_BRANCHES_KEY);

  const newBranches = branches.reduce(
    (acc, branch) => {
      acc[String(branch.id)] = branch;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_BRANCHES_KEY, {
    list: Object.values(newBranches),
    map: newBranches,
  });
};

export const getAll = (): Branches | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_BRANCHES_KEY);
};

export const find = async (branchId: string): Promise<Branch> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_BRANCHES_KEY);

  const branch = data?.map[branchId];
  if (branch) {
    return branch;
  }

  try {
    const newData = await queryClient.query(branchesQueryOptions({ search: { id: branchId } }));
    if (newData.map[branchId]) {
      void put(newData.list);
      return newData.map[branchId];
    }
  } catch {
    return getBranchFallback(branchId);
  }

  return getBranchFallback(branchId);
};

export const findAll = async (branchIds: string[]): Promise<Branch[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_BRANCHES_KEY);

  const missingIds: string[] = [];
  const branches: Branch[] = [];

  for (const branchId of branchIds) {
    const b = data?.map[branchId];
    if (b) {
      branches.push(b);
    } else {
      missingIds.push(branchId);
    }
  }

  if (!missingIds.length) {
    return branches;
  }

  try {
    const newData = await queryClient.query(branchesQueryOptions({ search: { id: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const b = newData.map[missingId];
      if (b) {
        branches.push(b);
      } else {
        branches.push(getBranchFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      branches.push(getBranchFallback(missingId));
    }
  }

  return branches;
};
